import { Box, Typography, Chip, Tooltip } from '@mui/material';
import React from 'react';
import { CommunicationHeaderProps } from '../types/types';
import { getCommIcon, statusColorMap } from '../helpers/helper';
import { getAvatar } from './GetAvatar';
import { borderAnimation } from '../styles/styles';
import { usePreformData } from '../../CommunicationModule/hooks/usePreformData';
import { Neutrals } from '../../../core-components/theme/color-palette/colors';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { isHTML } from '../../CommunicationModule/helpers/helper';
import { formatCommunicationTimestamp } from '../helpers/helper';
import { capitalizeFirstLetter } from '../../Triggers/helper';

function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

interface CommunicationHeaderWithAttachmentProps extends CommunicationHeaderProps {
  onAttachmentClick?: (attachment: any, event?: React.MouseEvent) => void;
}

const CommunicationHeader: React.FC<CommunicationHeaderWithAttachmentProps> = ({ communication, onAttachmentClick }) => {
  const { avatar, avatarBoxSx, borderWrapper } = getAvatar(communication);
  const { preformData } = usePreformData();
  let timeString = '';
  if (communication.timestamp) {
    timeString = formatCommunicationTimestamp(communication.timestamp);
  }

  const statusList = preformData.map((s) => s.status);
  const commStatus = communication.deliveryStatus;
  const showStatus = commStatus && statusList.includes(commStatus);
  const statusColor = statusColorMap[commStatus] || Neutrals[500];

  return (
    <>
      <style>{borderAnimation}</style>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
        <Box sx={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
          {borderWrapper}
          <Box sx={avatarBoxSx}>{avatar}</Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getCommIcon(communication.channel)}
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#183650' }}>
                {communication.messageHeader || 'Unknown'} 
              </Typography>

              {commStatus && (
              <>
              { showStatus && (
              <>
              <Typography variant='caption' sx={{ fontWeight: "bold", fontSize: "12px", color: Neutrals[300] }}>•</Typography>
              <Typography variant="body2" sx={{ fontWeight:400 ,fontSize: "12px", color: statusColor }}>
              {capitalizeFirstLetter(commStatus) || 'Unknown'}
              </Typography>
              </>
              )}
              </>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: '#8A94A6', fontWeight: 'normal', fontSize:"10px" }}>
              {timeString}
            </Typography>
          </Box>          
          {isHTML(communication.messageContent || "") ? (
            <Tooltip title={<span dangerouslySetInnerHTML={{ __html: communication.messageContent || "" }} />} placement="top" arrow>
              <Typography
                variant="body2"
                sx={{
                  color: '#183650',
                  opacity: 0.8,
                  fontWeight: 400,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  cursor: 'pointer',
                }}
                dangerouslySetInnerHTML={{ __html: communication.messageContent || "" }}
              />
            </Tooltip>
          ) : (
            <Tooltip title={communication.messageContent || ''} placement="top" arrow>
              <Typography
                variant="body2"
                sx={{
                  color: '#183650',
                  opacity: 0.8,
                  fontWeight: 400,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  cursor: 'pointer',
                }}
              >
                {communication.messageContent}
              </Typography>
            </Tooltip>
          )}
          {Array.isArray((communication as any).attachments) && (communication as any).attachments.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(communication as any).attachments.map((attachment: any, idx: number) => (
                <Chip
                  key={idx}
                  icon={<DescriptionOutlinedIcon sx={{fontSize:"13px"}}/>}
                  label={truncateText(attachment.name || attachment.link, 18)}
                  sx={{ 
                    maxWidth: 140, 
                    height: "20px", 
                    textOverflow: 'ellipsis', 
                    overflow: 'hidden', 
                    background: "#fff",
                    transition: 'background 0.2s',
                    '&:hover': {
                      background: Neutrals[200],
                    }
                  }}
                  onClick={(event) => onAttachmentClick && onAttachmentClick(attachment, event)}
                  clickable
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CommunicationHeader; 